import axios from 'axios';
import router from '@/router';
import store from '@/state/store';

// Base URL API Portal IHC
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

// Pesan default saat sesi habis / token tidak valid
const SESSION_EXPIRED_MESSAGE =
  'Sesi Anda telah berakhir. Silakan login kembali.';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Flag untuk mencegah multiple redirect saat beberapa request gagal bersamaan
let isHandlingUnauthorized = false;

/**
 * Force logout + redirect ke halaman login.
 * Dipanggil untuk SEMUA jenis kegagalan otentikasi/otorisasi yang artinya
 * user perlu login ulang (token kadaluarsa, token invalid, token tidak ada,
 * dll). Tidak peduli endpoint mana yang gagal — kalau token sudah tidak
 * valid, semua request setelahnya akan gagal juga, jadi langsung saja
 * arahkan user ke login.
 */
function handleUnauthorized() {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  // Bersihkan semua storage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('authUser');

  // Update Vuex state agar getter `auth/loggedIn` langsung false
  // (tanpa ini, route guard masih menganggap user logged in sampai reload)
  try {
    if (store && store.commit) {
      store.commit('auth/logout');
    }
  } catch (e) {
    // jika module auth belum ready, diabaikan saja
    console.warn('Failed to commit auth/logout:', e);
  }

  // Bersihkan menu state juga supaya saat user login lagi tidak ada
  // sisa data dari user sebelumnya.
  try {
    if (store && store.dispatch) {
      store.dispatch('menu/clear', null, { root: true });
    }
  } catch (e) {
    // abaikan jika module menu belum siap
  }

  // Cek apakah user sedang di halaman public (login/register/dll).
  // Kalau iya, tidak perlu notifikasi atau redirect — banner di halaman
  // login sudah cukup memberitahu user.
  const publicPages = ['/login', '/register', '/forgot-password'];
  const currentPath = router.currentRoute.value
    ? router.currentRoute.value.path
    : window.location.pathname;
  const isOnPublicPage = publicPages.includes(currentPath);

  // Tampilkan notifikasi session expired hanya kalau user sedang di halaman
  // protected. Kalau sudah di /login (mis. token sudah expired sebelumnya
  // dan user kebetulan ada di halaman login), banner sessionExpired di
  // login.vue sudah cukup — notifikasi tambahan malah redundant.
  if (!isOnPublicPage) {
    try {
      if (store && store.dispatch) {
        store.dispatch('notification/error', SESSION_EXPIRED_MESSAGE, {
          root: true,
        });
      }
    } catch (e) {
      // abaikan jika notification module belum siap
    }
  }

  if (!isOnPublicPage) {
    router
      .push({
        name: 'login',
        query: {
          redirectFrom: currentPath,
          // Flag ini dipakai halaman login untuk menampilkan banner
          // "Sesi Anda telah berakhir" alih-alih halaman login kosong.
          sessionExpired: '1',
        },
      })
      .catch(() => {
        // abaikan error navigation duplicate
      });
  }

  // Reset flag setelah short delay agar tidak stuck
  setTimeout(() => {
    isHandlingUnauthorized = false;
  }, 1000);
}

// Request interceptor - attach token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Cek apakah error ini menandakan masalah otentikasi/otorisasi yang
 * mengharuskan user login ulang.
 *
 * Kita anggap session expired untuk:
 *   - HTTP 401 Unauthorized        -> token kadaluarsa / tidak valid
 *   - HTTP 419 Authentication Timeout (Laravel) -> session expired
 *   - HTTP 440 Login Time-out      -> session expired (varian)
 *   - HTTP 403 yang pesannya menyebut token/unauthenticated/invalid token
 *     -> beberapa backend pakai 403 untuk token invalid alih-alih 401
 *
 * 403 polos (forbidden karena role tidak punya akses) TIDAK dianggap session
 * expired — user tetap logged in, hanya tidak punya hak.
 */
function isAuthFailure(error) {
  const status = error?.response?.status;
  if (status === 401 || status === 419 || status === 440) return true;

  if (status === 403) {
    const data = error?.response?.data || {};
    const msg = String(data.message || data.error || '').toLowerCase();
    if (
      msg.includes('token') ||
      msg.includes('unauthenticated') ||
      msg.includes('unauthorized') ||
      msg.includes('expired') ||
      msg.includes('invalid signature')
    ) {
      return true;
    }
  }

  return false;
}

// Response interceptor - handle auth failures globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network error / timeout / request dibatalkan: tidak ada response.
    // Jangan trigger logout — bisa jadi masalah jaringan sementara.
    if (!error.response) {
      return Promise.reject(error);
    }

    const requestUrl = (error.config && error.config.url) || '';

    // Endpoint auth tidak boleh memicu auto-logout:
    //   - /auth/login: 401/403 di sini artinya kredensial salah, BUKAN session expired.
    //     Kalau kita panggil handleUnauthorized() di sini, halaman login bakal
    //     reload terus dan banner "sesi berakhir" muncul padahal user belum
    //     pernah login.
    //   - /auth/logout: kalaupun server menolak, kita tetap mau lanjut clear
    //     local storage di authService.logout — tidak perlu redirect ulang.
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/logout');

    if (!isAuthEndpoint && isAuthFailure(error)) {
      handleUnauthorized();
    }

    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL, SESSION_EXPIRED_MESSAGE };